import React from "react";
import { FormattedMessage, injectIntl, defineMessages, intlShape } from "react-intl";
import { ChatBubbleOvalLeftIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import Link from "../Link";
import { Badge, Button, Card, Title } from "@yoast/ui-library";
import * as styles from "./SupportCardStyles.module.scss";

const HELP_CENTER_URL = "https://yoast.com/help/";

const messages = defineMessages( {
	supportHeader: {
		id: "support.card.header",
		defaultMessage: "Support",
	},
	supportIntro: {
		id: "support.card.intro",
		defaultMessage: "If you have a question, if you need help, or if you just want to contact us, " +
			"there are several ways to get in touch.",
	},
	supportChatButton: {
		id: "support.card.chatButton",
		defaultMessage: "Chat with us",
	},
	supportPremiumBadge: {
		id: "support.card.premiumBadge",
		defaultMessage: "Premium",
	},
	supportHelpCenterButton: {
		id: "support.card.helpCenterButton",
		defaultMessage: "Visit Help center",
	},
	supportPopularTopicsHeader: {
		id: "support.card.popularTopicsHeader",
		defaultMessage: "Popular Help topics",
	},
	supportTopicXmlSitemap: {
		id: "support.card.topicXmlSitemap",
		defaultMessage: "XML Sitemap",
	},
	supportTopicAiFeatures: {
		id: "support.card.topicAiFeatures",
		defaultMessage: "AI features",
	},
	supportTopicSchema: {
		id: "support.card.topicSchema",
		defaultMessage: "Schema",
	},
} );

const popularTopics = [
	{
		message: messages.supportTopicXmlSitemap,
		url: "https://yoast.com/help/xml-sitemaps-in-the-wordpress-seo-plugin/",
	},
	{
		message: messages.supportTopicAiFeatures,
		url: "https://yoast.com/help/faq-ai-in-yoast-seo/",
	},
	{
		message: messages.supportTopicSchema,
		url: "https://yoast.com/help/implementing-schema-with-yoast-seo/",
	},
];

/**
 * Opens the Help Scout beacon.
 *
 * The beacon script is injected via Google Tag Manager, so it is absent in development
 * and when blocked by the browser; in that case clicking does nothing.
 *
 * @returns {void}
 */
const openBeacon = () => {
	const beacon = window.Beacon;
	if ( typeof beacon === "function" ) {
		beacon( "open" );
	}
};

/**
 * A function that returns the SupportCard component.
 *
 * @param {Object} props The props required for the SupportCard component.
 *
 * @returns {ReactElement} The SupportCard component.
 */
const SupportCard = () => {
	return (
		<Card>
			<div className={ styles.supportCardHeader }>
				<Title as="h2" size="4">
					<FormattedMessage { ...messages.supportHeader } />
				</Title>
				<p>
					<FormattedMessage { ...messages.supportIntro } />
				</p>
			</div>
			<Card.Content className={ styles.supportCardContent }>
				<div className={ styles.supportButtonWrapper }>
					<Button
						id="support-chat-button"
						onClick={ openBeacon }
						className={ styles.supportButton }
					>
						<ChatBubbleOvalLeftIcon className={ styles.supportButtonIcon } />
						<FormattedMessage { ...messages.supportChatButton } />
						<Badge as="span" variant="upsell">
							<FormattedMessage { ...messages.supportPremiumBadge } />
						</Badge>
					</Button>
					<Button
						id="support-help-center-button"
						as="a"
						variant="secondary"
						href={ HELP_CENTER_URL }
						target="_blank"
						rel="noopener noreferrer"
						className={ styles.supportButton }
					>
						<FormattedMessage { ...messages.supportHelpCenterButton } />
						<ArrowTopRightOnSquareIcon className={ styles.supportButtonIcon } />
					</Button>
				</div>
				<div className={ styles.supportTopics }>
					<Title as="h3" size="5">
						<FormattedMessage { ...messages.supportPopularTopicsHeader } />
					</Title>
					<ul className={ styles.supportTopicsList }>
						{ popularTopics.map( topic => (
							<li key={ topic.message.id }>
								<Link to={ topic.url } linkTarget="_blank" linkRel="noopener noreferrer">
									<FormattedMessage { ...topic.message } />
								</Link>
							</li>
						) ) }
					</ul>
				</div>
			</Card.Content>
		</Card>
	);
};

SupportCard.propTypes = {
	intl: intlShape.isRequired,
};

export default injectIntl( SupportCard );
